import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { addMinutes, format, isBefore, startOfDay } from "date-fns";
import { ArrowLeft, AlertCircle, Loader2, CheckCircle2, Calendar as CalendarIcon, Clock3, XCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { sendOwnerPush } from "@/lib/owner-push";
import {
  areServicesAvailableOnDay,
  doesSlotFitServiceHours,
  minutesToTime,
  timeToMinutes,
} from "@/lib/service-hours";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type BookingDetails = {
  appointment_id: string;
  business_id: string;
  service_id: string;
  staff_id: string | null;
  customer_id: string;
  start_datetime: string;
  end_datetime: string;
  status: string;
  notes: string | null;
  confirmation_code: string;
  rescheduled_from_start_datetime?: string | null;
  rescheduled_from_end_datetime?: string | null;
  reschedule_reason?: string | null;
  rescheduled_at?: string | null;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  service_name: string;
  service_duration_minutes: number;
  service_price_amount: number;
  service_currency: string | null;
  staff_name: string | null;
  total_price_amount?: number | null;
  appointment_currency?: string | null;
  addons?: Array<{ name: string; price_amount: number; quantity?: number; currency?: string | null }> | any;
  appointment_group_id?: string | null;
  group_start_datetime?: string | null;
  group_end_datetime?: string | null;
  group_total_duration_minutes?: number | null;
  group_total_price_amount?: number | null;
  group_services?: any;
};

type ProductOrderDetails = {
  id: string;
  confirmation_code: string;
  customer_name: string;
  customer_phone: string | null;
  customer_email: string | null;
  fulfillment_type: string;
  delivery_address: string | null;
  delivery_place_id: string | null;
  delivery_lat: number | null;
  delivery_lng: number | null;
  status: string;
  total_amount: number;
  currency: string;
  notes: string | null;
  created_at: string;
  items: Array<{ id: string; product_name: string; quantity: number; unit_price: number; line_total: number }>;
};

function titleCase(input: string) {
  return input.split(" ").filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
}

function productOrderStatusLabel(raw: string) {
  return titleCase(String(raw || "new").replaceAll("_", " "));
}

function ProductOrderStatusChip({ status }: { status: string }) {
  const s = String(status || "new");
  const label = productOrderStatusLabel(s);
  const cls =
    s === "new" ? "bg-blue-50 text-blue-700 border-blue-200"
    : s === "confirmed" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : s === "preparing" ? "bg-amber-50 text-amber-700 border-amber-200"
    : s === "ready" ? "bg-violet-50 text-violet-700 border-violet-200"
    : s === "out_for_delivery" ? "bg-sky-50 text-sky-700 border-sky-200"
    : s === "delivered" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : s === "cancelled" ? "bg-slate-50 text-slate-600 border-slate-200"
    : "bg-muted text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border ${cls}`}>
      {label}
    </span>
  );
}

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    GHS: "₵",
    NGN: "₦",
    USD: "$",
    EUR: "€",
    GBP: "£",
    ZAR: "R",
    KES: "KSh",
  };
  return symbols[currency] || currency;
};

const generateTimeSlots = (openTime: string, closeTime: string, intervalMinutes: number = 30): string[] => {
  const slots: string[] = [];
  const [openHour, openMinute] = openTime.split(":").map(Number);
  const [closeHour, closeMinute] = closeTime.split(":").map(Number);

  let currentMinutes = openHour * 60 + openMinute;
  const closeMinutes = closeHour * 60 + closeMinute;

  while (currentMinutes < closeMinutes) {
    const hours = Math.floor(currentMinutes / 60);
    const minutes = currentMinutes % 60;
    slots.push(`${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`);
    currentMinutes += intervalMinutes;
  }

  return slots;
};

function DepositPaymentInfo({
  appointmentId,
  groupId,
  currency,
}: {
  appointmentId: string;
  groupId?: string | null;
  currency: string;
}) {
  const [depositPayment, setDepositPayment] = useState<{
    amount: number;
    status: string;
    paid_at: string | null;
    currency: string;
  } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDeposit = async () => {
      try {
        const isGroup = !!groupId;
        const { data, error } = await supabase
          .from("platform_payments")
          .select("gross_amount, status, paid_at, currency")
          .eq("context", isGroup ? "appointment_group_deposit" : "appointment_deposit")
          .eq("context_id", isGroup ? groupId : appointmentId)
          .eq("status", "success")
          .order("paid_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        if (data) {
          setDepositPayment({
            amount: Number(data.gross_amount || 0),
            status: data.status,
            paid_at: data.paid_at,
            currency: data.currency || "GHS",
          });
        }
      } catch (err) {
        console.error("Failed to fetch deposit payment:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDeposit();
  }, [appointmentId, groupId]);

  if (loading || !depositPayment) return null;

  return (
    <div className="rounded-lg border border-success/20 bg-success/5 p-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-success" />
          <span className="text-sm font-medium text-foreground">Deposit Paid</span>
        </div>
        <span className="font-semibold text-success">
          {currency}{depositPayment.amount.toFixed(2)}
        </span>
      </div>
      {depositPayment.paid_at && (
        <p className="text-xs text-muted-foreground mt-1">
          Paid on {format(new Date(depositPayment.paid_at), "MMM d, yyyy 'at' HH:mm")}
        </p>
      )}
    </div>
  );
}

export default function ManageBooking() {
  const { businessSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const initialCode = (searchParams.get("code") || "").trim().toUpperCase();
  const [code, setCode] = useState(initialCode);
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState<BookingDetails | null>(null);
  const [orderDetails, setOrderDetails] = useState<ProductOrderDetails | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [businessHours, setBusinessHours] = useState<any[]>([]);
  const [serviceHours, setServiceHours] = useState<any[]>([]);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [timeSlots, setTimeSlots] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState<string>("");
  const [confirmNoReasonOpen, setConfirmNoReasonOpen] = useState(false);

  const today = startOfDay(new Date());

  const currencySymbol = useMemo(() => {
    const currency = (details as any)?.appointment_currency || details?.service_currency || "GHS";
    return getCurrencySymbol(currency);
  }, [details?.service_currency, (details as any)?.appointment_currency]);

  const addons = useMemo(() => {
    const raw = (details as any)?.addons;
    if (!raw) return [] as Array<{ name: string; price_amount: number; quantity: number }>;
    // RPC returns JSONB; PostgREST may already parse to JS objects
    if (Array.isArray(raw)) {
      return raw.map((a: any) => ({
        name: String(a?.name || ""),
        price_amount: Number(a?.price_amount || 0),
        quantity: Math.max(1, Number(a?.quantity || 1)),
      })).filter((a) => a.name);
    }
    return [];
  }, [details]);

  const groupServices = useMemo(() => {
    const raw = (details as any)?.group_services;
    if (!raw) return [] as any[];
    if (Array.isArray(raw)) return raw as any[];
    return [];
  }, [details]);

  const isBundle = !!(details as any)?.appointment_group_id && groupServices.length > 0;

  const servicePrice = Number(details?.service_price_amount || 0);
  const addonsTotal = addons.reduce((sum, a) => sum + a.price_amount * a.quantity, 0);
  const totalPrice = isBundle
    ? Number((details as any)?.group_total_price_amount ?? 0)
    : Number((details as any)?.total_price_amount ?? (servicePrice + addonsTotal));

  const fetchBooking = async (overrideCode?: string) => {
    if (!businessSlug) return;
    const lookupCode = (overrideCode ?? code).trim().toUpperCase();
    if (!lookupCode) {
      toast({ title: "Enter your reference code", variant: "destructive" });
      return;
    }

    setLoading(true);
    setNotFound(false);
    setOrderDetails(null);
    try {
      const { data, error } = await supabase.rpc("get_booking_by_code", {
        _business_slug: businessSlug,
        _code: lookupCode,
      });

      if (error) throw error;
      let row = Array.isArray(data) ? data[0] : data;
      if (row) {
        setDetails(row as BookingDetails);
        setRescheduleOpen(false);
        setSelectedDate(null);
        setSelectedTime(null);
        setRescheduleReason("");
        setLoading(false);
        return;
      }

      const { data: orderData, error: orderError } = await supabase.rpc("get_product_order_by_code", {
        _business_slug: businessSlug,
        _code: lookupCode,
      });
      if (orderError) throw orderError;
      const orderRow = orderData as ProductOrderDetails | null;
      if (orderRow) {
        setDetails(null);
        setOrderDetails(orderRow);
        setLoading(false);
        return;
      }

      setDetails(null);
      setNotFound(true);
    } catch (err: any) {
      console.error("lookup by code error:", err);
      toast({
        title: "Could not find booking",
        description: err?.message || "Please check the code and try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch if we have a code in URL
  useEffect(() => {
    if (initialCode) {
      fetchBooking(initialCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load business hours after details are loaded (needs business_id)
  useEffect(() => {
    const fetchHours = async () => {
      if (!details?.business_id) return;
      try {
        const { data, error } = await supabase
          .from("business_hours")
          .select("*")
          .eq("business_id", details.business_id)
          .is("location_id", null)
          .order("day_of_week");
        if (error) throw error;
        setBusinessHours(data || []);
      } catch (err) {
        console.error("Error fetching business hours:", err);
      }
    };
    fetchHours();
  }, [details?.business_id]);

  useEffect(() => {
    const fetchServiceHours = async () => {
      if (!details?.business_id) {
        setServiceHours([]);
        return;
      }
      const serviceIds = isBundle
        ? groupServices.map((service: any) => String(service?.service_id || "")).filter(Boolean)
        : [details.service_id].filter(Boolean);
      if (serviceIds.length === 0) {
        setServiceHours([]);
        return;
      }
      const { data, error } = await supabase
        .from("service_hours")
        .select("*")
        .eq("business_id", details.business_id)
        .in("service_id", serviceIds);
      if (error) {
        console.error("Error fetching service hours:", error);
        setServiceHours([]);
        return;
      }
      setServiceHours(data || []);
    };
    void fetchServiceHours();
  }, [details?.business_id, details?.service_id, isBundle, groupServices]);

  const rescheduleServices = useMemo(() => {
    if (isBundle) {
      return groupServices.map((service: any) => ({
        id: String(service?.service_id || ""),
        duration: Math.max(1, Number(service?.duration_minutes) || 30),
      })).filter((service) => service.id);
    }
    return details?.service_id
      ? [{ id: details.service_id, duration: Math.max(1, Number(details.service_duration_minutes) || 30) }]
      : [];
  }, [details?.service_id, details?.service_duration_minutes, groupServices, isBundle]);

  const servicesAvailableOnDay = (dayOfWeek: number) =>
    areServicesAvailableOnDay(rescheduleServices, serviceHours, dayOfWeek);

  const slotFitsServiceHours = (slot: string, dayOfWeek: number) =>
    doesSlotFitServiceHours(slot, rescheduleServices, serviceHours, dayOfWeek);

  // Update time slots based on selected date and business hours
  useEffect(() => {
    const fetchBundleSlots = async () => {
      if (!selectedDate || !details?.business_id) return;
      try {
        const serviceIds = groupServices.map((s: any) => String(s?.service_id || "")).filter(Boolean);
        if (serviceIds.length === 0) return;
        const dateStr = format(selectedDate, "yyyy-MM-dd");
        const { data, error } = await (supabase as any).rpc("get_multi_service_time_slots_public", {
          p_business_id: details.business_id,
          p_location_id: null,
          p_date: dateStr,
          p_service_ids: serviceIds,
          p_interval_minutes: 30,
        });
        if (error) throw error;
        const slots = (data || [])
          .map((r: any) => String(r?.slot_time ?? r?.time ?? r))
          .filter(Boolean)
          .filter((slot: string) => slotFitsServiceHours(slot, selectedDate.getDay()));
        setTimeSlots(slots);
      } catch (e) {
        console.error("Failed to fetch bundle slots:", e);
        setTimeSlots([]);
      }
    };

    if (!selectedDate) {
      setTimeSlots(generateTimeSlots("09:00", "17:00", 30));
      return;
    }

    if (isBundle) {
      setTimeSlots([]);
      void fetchBundleSlots();
      return;
    }

    const dayOfWeek = selectedDate.getDay();
    const dayHours = businessHours.find((h) => h.day_of_week === dayOfWeek) || (
      businessHours.length === 0
        ? { open_time: "09:00", close_time: "17:00", is_closed: false }
        : null
    );
    if (!dayHours || dayHours.is_closed) {
      setTimeSlots([]);
      return;
    }

    const rows = serviceHours.filter((hour) => hour.service_id === details?.service_id);
    if (rows.length > 0) {
      const serviceHour = rows.find((hour) => hour.day_of_week === dayOfWeek);
      if (!serviceHour) {
        setTimeSlots([]);
        return;
      }
      const open = Math.max(timeToMinutes(dayHours.open_time), timeToMinutes(serviceHour.start_time));
      const close = Math.min(timeToMinutes(dayHours.close_time), timeToMinutes(serviceHour.end_time));
      if (open >= close) {
        setTimeSlots([]);
        return;
      }
      setTimeSlots(
        generateTimeSlots(minutesToTime(open), minutesToTime(close), 30)
          .filter((slot) => timeToMinutes(slot) + Math.max(1, Number(details?.service_duration_minutes) || 30) <= close),
      );
      return;
    }

    setTimeSlots(
      generateTimeSlots(dayHours.open_time, dayHours.close_time, 30)
        .filter((slot) =>
          timeToMinutes(slot) + Math.max(1, Number(details?.service_duration_minutes) || 30)
          <= timeToMinutes(dayHours.close_time)
        ),
    );
  }, [selectedDate, businessHours, isBundle, groupServices, details?.business_id, details?.service_id, details?.service_duration_minutes, serviceHours, rescheduleServices]);

  const reschedule = async () => {
    if (!businessSlug || !details) return;
    if (!selectedDate || !selectedTime) {
      toast({ title: "Select a date and time", variant: "destructive" });
      return;
    }

    const reason = rescheduleReason.trim();
    if (!reason) {
      // soft-mandatory: nudge, but allow with confirmation
      setConfirmNoReasonOpen(true);
      return;
    }

    await doReschedule(reason);
  };

  const doReschedule = async (reason: string | null) => {
    setIsSaving(true);
    try {
      const [h, m] = selectedTime.split(":").map(Number);
      const start = new Date(selectedDate);
      start.setHours(h, m, 0, 0);
      const durationMinutes = isBundle
        ? Number((details as any)?.group_total_duration_minutes || 0)
        : Number(details.service_duration_minutes || 0);
      const end = addMinutes(start, durationMinutes || 30);

      const { data, error } = await supabase.rpc("reschedule_booking_by_code", {
        _business_slug: businessSlug,
        _code: details.confirmation_code,
        _new_start: start.toISOString(),
        _new_end: end.toISOString(),
        _reason: reason,
      });

      if (error) throw error;
      if (!data) {
        toast({ title: "Reschedule failed", description: "Booking not found.", variant: "destructive" });
        return;
      }

      toast({ title: "Booking rescheduled", description: "Your booking has been updated." });

      // Fire-and-forget reschedule email to customer
      supabase.functions
        .invoke("send-email", {
          body: {
            type: "rescheduled",
            appointmentId: details.appointment_id,
            confirmationCode: details.confirmation_code,
          },
        })
        .then(({ error }) => {
          if (error) console.warn("send-email rescheduled failed:", error);
        })
        .catch((e) => console.warn("send-email rescheduled crashed:", e));

      await sendOwnerPush({
        businessId: details.business_id,
        title: "Appointment rescheduled",
        body: `${details.customer_name} rescheduled booking ${details.confirmation_code} to ${format(start, "EEE, MMM d 'at' h:mm a")}.`,
        url: "/app/appointments",
        data: {
          context: "appointment_rescheduled",
          context_id: details.appointment_id,
          confirmation_code: details.confirmation_code,
        },
      });

      await fetchBooking(details.confirmation_code);
    } catch (err: any) {
      console.error("reschedule error:", err);
      toast({ title: "Reschedule failed", description: err?.message || "Try again.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  const cancelBooking = async () => {
    if (!businessSlug || !details) return;
    setIsSaving(true);
    try {
      const { data, error } = await supabase.rpc("cancel_booking_by_code", {
        _business_slug: businessSlug,
        _code: details.confirmation_code,
      });

      if (error) throw error;
      if (!data) {
        toast({ title: "Cancel failed", description: "Booking not found.", variant: "destructive" });
        return;
      }

      toast({ title: "Booking cancelled" });

      await sendOwnerPush({
        businessId: details.business_id,
        title: "Appointment status changed",
        body: `${details.customer_name} cancelled booking ${details.confirmation_code}.`,
        url: "/app/appointments",
        data: {
          context: "appointment_status_changed",
          context_id: details.appointment_id,
          confirmation_code: details.confirmation_code,
          new_status: "cancelled",
        },
      });

      await fetchBooking(details.confirmation_code);
    } catch (err: any) {
      console.error("cancel error:", err);
      toast({ title: "Cancel failed", description: err?.message || "Try again.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="container-app py-4">
          <div className="flex items-center gap-4">
            <Link
              to={`/book/${businessSlug}`}
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm">Back</span>
            </Link>
            <div className="h-4 w-px bg-border" />
            <h1 className="font-semibold text-foreground">{orderDetails ? "Manage Order" : "Manage Booking"}</h1>
          </div>
        </div>
      </header>

      <main className="container-app py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          {!orderDetails && !details && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground">Find your booking or order</h2>
            <p className="text-muted-foreground mt-1">
              Enter your 6‑character reference code to view, reschedule (bookings), or check status (orders).
            </p>
          </div>
          )}

          <div className="bg-card rounded-xl border border-border p-4 sm:p-6">
            <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
              <div className="space-y-2">
                <Label htmlFor="code">Reference code</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="ABC123"
                  maxLength={6}
                  className="font-mono tracking-widest"
                />
              </div>
              <Button
                onClick={() => fetchBooking()}
                disabled={loading || code.trim().length !== 6}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Looking up...
                  </>
                ) : (
                  "Search"
                )}
              </Button>
            </div>

            {notFound && (
              <div className="mt-4 flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/10 p-3">
                <AlertCircle className="h-5 w-5 text-destructive mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">Booking or order not found</p>
                  <p className="text-destructive/90">
                    Check the code and try again. Codes are 6 characters long.
                  </p>
                </div>
              </div>
            )}
          </div>

          {orderDetails && (
            <div className="bg-card rounded-xl border border-border p-4 sm:p-6 space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center flex-wrap gap-2">
                    <p className="text-xs text-muted-foreground">Order</p>
                    <p className="font-mono text-lg font-semibold tracking-widest text-foreground">
                      #{orderDetails.confirmation_code}
                    </p>
                    <ProductOrderStatusChip status={orderDetails.status} />
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border ${
                      orderDetails.fulfillment_type === "delivery"
                        ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                        : "bg-slate-50 text-slate-700 border-slate-200"
                    }`}>
                      {orderDetails.fulfillment_type === "delivery" ? "Delivery" : "Pickup"}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    Placed {format(new Date(orderDetails.created_at), "EEE, MMM d, yyyy 'at' HH:mm")}
                  </p>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                <div className="text-sm font-semibold text-foreground">Items</div>
                <div className="space-y-2">
                  {(orderDetails.items || []).map((it: any) => (
                    <div key={it.id} className="flex items-start justify-between gap-4 text-sm">
                      <div className="min-w-0">
                        <div className="font-medium text-foreground">{it.product_name}</div>
                        <div className="text-xs text-muted-foreground">
                          Qty: {Number(it.quantity || 1)} × {getCurrencySymbol(orderDetails.currency)}{Number(it.unit_price || 0).toFixed(2)}
                        </div>
                      </div>
                      <div className="font-semibold text-foreground whitespace-nowrap">
                        {getCurrencySymbol(orderDetails.currency)}{Number(it.line_total || 0).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
                  <span className="font-semibold text-foreground">Total</span>
                  <span className="font-semibold text-foreground text-lg">
                    {getCurrencySymbol(orderDetails.currency)}{Number(orderDetails.total_amount || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              {orderDetails.fulfillment_type === "delivery" && orderDetails.delivery_address && (
                <div className="rounded-xl border border-border bg-muted/20 p-4">
                  <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Delivery address</div>
                  <p className="text-sm text-foreground">{orderDetails.delivery_address}</p>
                  {orderDetails.delivery_place_id || (orderDetails.delivery_lat != null && orderDetails.delivery_lng != null) ? (
                    <a
                      href={`https://www.google.com/maps/search/?api=1${
                        orderDetails.delivery_place_id
                          ? `&query_place_id=${encodeURIComponent(orderDetails.delivery_place_id)}`
                          : orderDetails.delivery_lat != null && orderDetails.delivery_lng != null
                            ? `&query=${encodeURIComponent(`${orderDetails.delivery_lat},${orderDetails.delivery_lng}`)}`
                            : orderDetails.delivery_address
                              ? `&query=${encodeURIComponent(orderDetails.delivery_address)}`
                              : ""
                      }`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-2"
                    >
                      View on map
                    </a>
                  ) : null}
                </div>
              )}

              {orderDetails.notes && (
                <div className="text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">Order notes:</span> {orderDetails.notes}
                </div>
              )}

              {orderDetails.status === "cancelled" && (
                <div className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/10 p-3">
                  <XCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">This order has been cancelled.</p>
                </div>
              )}
            </div>
          )}

          {details && (
            <div className="space-y-4">
              <div className="bg-card rounded-xl border border-border p-4 sm:p-6">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Reference</p>
                    <p className="font-mono text-lg font-semibold tracking-widest text-foreground">
                      {details.confirmation_code}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {details.status === "cancelled" ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-destructive/10 text-destructive px-3 py-1 text-xs font-medium border border-destructive/20">
                        <XCircle className="h-3.5 w-3.5" />
                        Cancelled
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 text-success px-3 py-1 text-xs font-medium border border-success/20">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {details.status}
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-4 grid gap-3 text-sm">
                  {(details as any).rescheduled_from_start_datetime && (
                    <div className="rounded-lg border border-border bg-accent/40 p-3">
                      <p className="text-xs text-muted-foreground mb-2">Rescheduled</p>
                      <div className="grid gap-1 text-sm">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-muted-foreground">From</span>
                          <span className="font-medium text-foreground text-right">
                            {format(new Date((details as any).rescheduled_from_start_datetime), "EEE, MMM d, yyyy")} •{" "}
                            {format(new Date((details as any).rescheduled_from_start_datetime), "HH:mm")}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-muted-foreground">To</span>
                          <span className="font-medium text-foreground text-right">
                            {format(new Date(details.start_datetime), "EEE, MMM d, yyyy")} •{" "}
                            {format(new Date(details.start_datetime), "HH:mm")}
                          </span>
                        </div>
                        {(details as any).reschedule_reason && (
                          <div className="pt-2 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">Reason:</span>{" "}
                            {(details as any).reschedule_reason}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  {isBundle ? (
                    <div className="rounded-lg border border-border bg-accent/30 p-3">
                      <p className="text-xs text-muted-foreground mb-2">Services</p>
                      <div className="space-y-2">
                        {groupServices.map((s: any) => (
                          <div key={String(s?.appointment_id || s?.service_id)} className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="font-medium text-foreground truncate">{String(s?.service_name || "Service")}</p>
                              {s?.start_datetime ? (
                                <p className="text-xs text-muted-foreground">
                                  {format(new Date(String(s.start_datetime)), "HH:mm")}–{format(new Date(String(s.end_datetime)), "HH:mm")}
                                  {s?.staff_name ? ` • ${String(s.staff_name)}` : ""}
                                </p>
                              ) : null}
                            </div>
                            {s?.total_price_amount != null ? (
                              <div className="text-right">
                                <p className="font-semibold text-foreground">
                                  {currencySymbol}
                                  {Number(s.total_price_amount || 0).toFixed(0)}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">Service</span>
                      <span className="font-medium text-foreground text-right">{details.service_name}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Price</span>
                    <span className="font-semibold text-foreground">
                      {currencySymbol}
                      {Number(totalPrice || 0).toFixed(0)}
                    </span>
                  </div>
                  {!isBundle && addons.length > 0 && (
                    <div className="rounded-lg border border-border bg-accent/30 p-3">
                      <p className="text-xs text-muted-foreground mb-2">Add-ons</p>
                      <div className="space-y-1">
                        {addons.map((a, idx) => (
                          <div key={`${a.name}-${idx}`} className="flex items-center justify-between gap-3 text-sm">
                            <span className="text-foreground">
                              {a.name} <span className="text-muted-foreground">× {a.quantity}</span>
                            </span>
                            <span className="text-muted-foreground">
                              +{currencySymbol}{(a.price_amount * a.quantity).toFixed(0)}
                            </span>
                          </div>
                        ))}
                        <div className="pt-2 mt-2 border-t border-border flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Add-ons total</span>
                          <span className="font-medium text-foreground">
                            {currencySymbol}{addonsTotal.toFixed(0)}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-semibold text-foreground">
                      {currencySymbol}{Number(totalPrice || 0).toFixed(0)}
                    </span>
                  </div>
                  {!isBundle && details?.appointment_id && (
                    <DepositPaymentInfo appointmentId={details.appointment_id} currency={currencySymbol} />
                  )}
                  {isBundle && details?.appointment_id && (
                    <DepositPaymentInfo
                      appointmentId={details.appointment_id}
                      groupId={(details as any)?.appointment_group_id || null}
                      currency={currencySymbol}
                    />
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">When</span>
                    <span className="font-medium text-foreground text-right">
                      {format(new Date(details.start_datetime), "EEE, MMM d, yyyy")} •{" "}
                      {format(new Date(details.start_datetime), "HH:mm")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">Staff</span>
                    <span className="font-medium text-foreground text-right">
                      {isBundle
                        ? (() => {
                            const names = Array.from(
                              new Set(
                                groupServices
                                  .map((s: any) => String(s?.staff_name || ""))
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              ),
                            );
                            if (names.length === 0) return "Any available";
                            if (names.length === 1) return names[0];
                            return `${names.length} staff`;
                          })()
                        : details.staff_name || "Any available"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="grid gap-3 sm:grid-cols-2">
                <Button
                  variant="outline"
                  className="w-full"
                  disabled={details.status === "cancelled"}
                  onClick={() => setRescheduleOpen((v) => !v)}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {rescheduleOpen ? "Close Reschedule" : "Reschedule"}
                </Button>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="w-full" disabled={details.status === "cancelled" || isSaving}>
                      <XCircle className="mr-2 h-4 w-4" />
                      Cancel Booking
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel this booking?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will mark the booking as cancelled. You can’t undo this action.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel disabled={isSaving}>Keep booking</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={cancelBooking}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {isSaving ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Cancelling...
                          </>
                        ) : (
                          "Cancel booking"
                        )}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              {/* Reschedule UI (reuses Calendar + time slots pattern) */}
              {rescheduleOpen && (
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="p-4 sm:p-6 space-y-4">
                    <div className="flex items-center gap-2">
                      <Clock3 className="h-4 w-4 text-muted-foreground" />
                      <h3 className="font-semibold text-foreground">Pick a new time</h3>
                    </div>

                    <div className="bg-card rounded-xl border border-border overflow-hidden p-4">
                      <Calendar
                        mode="single"
                        selected={selectedDate || undefined}
                        onSelect={(date) => {
                          setSelectedDate(date || null);
                          setSelectedTime(null);
                        }}
                        disabled={(date) => {
                          if (isBefore(date, today)) return true;
                          if (businessHours.length > 0) {
                            const dayOfWeek = date.getDay();
                            const dayHours = businessHours.find((h) => h.day_of_week === dayOfWeek);
                            if (!dayHours || dayHours.is_closed) return true;
                          }
                          if (!servicesAvailableOnDay(date.getDay())) return true;
                          return false;
                        }}
                        className="w-full max-w-full [&_.rdp]:w-full [&_.rdp-months]:w-full [&_.rdp-month]:w-full [&_.rdp-table]:w-full [&_table]:w-full [&_thead]:w-full [&_tbody]:w-full [&_tr]:grid [&_tr]:grid-cols-7 [&_tr]:w-full [&_td]:flex [&_td]:justify-center [&_th]:flex [&_th]:justify-center [&_.rdp-cell]:flex-1 [&_.rdp-day]:w-full [&_.rdp-day]:h-12 [&_.rdp-day]:text-base"
                      />
                    </div>

                    {selectedDate && (
                      <div className="bg-card rounded-xl border border-border p-4">
                        <h4 className="font-medium text-foreground mb-3">
                          Available times for {format(selectedDate, "EEEE, MMMM d")}
                        </h4>
                        {timeSlots.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-2">
                            No available times for this day.
                          </p>
                        ) : (
                          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                            {timeSlots.map((time) => (
                              <button
                                key={time}
                                onClick={() => setSelectedTime(time)}
                                className={cn(
                                  "py-2 px-3 rounded-lg text-sm font-medium transition-colors",
                                  selectedTime === time
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-accent hover:bg-accent/80 text-foreground",
                                )}
                              >
                                {time}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="reschedule-reason">Reason for reschedule</Label>
                        <span className="text-xs text-muted-foreground">Recommended</span>
                      </div>
                      <Textarea
                        id="reschedule-reason"
                        value={rescheduleReason}
                        onChange={(e) => setRescheduleReason(e.target.value)}
                        placeholder="E.g. Something came up, please move it to the new time."
                        className="min-h-[90px]"
                      />
                      <p className="text-xs text-muted-foreground">
                        This will be shared with the business. If you don’t want to add one, you can continue without a reason.
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-border p-4 sm:p-6 flex flex-col sm:flex-row gap-3 sm:justify-end">
                    <Button variant="outline" onClick={() => setRescheduleOpen(false)} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button onClick={reschedule} disabled={isSaving || !selectedDate || !selectedTime}>
                      {isSaving ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Confirm Reschedule"
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Soft-mandatory confirmation when reason is empty */}
              <AlertDialog open={confirmNoReasonOpen} onOpenChange={setConfirmNoReasonOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reschedule without a reason?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Adding a short reason helps the business understand the change. You can continue without one if needed.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={isSaving}>Add reason</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => doReschedule(null)}
                      disabled={isSaving}
                    >
                      Continue
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}


